<template>
  <div class="loading-screen" :class="{ 'fade-out': isLoaded }">
    <div class="loading-container">
      <!-- MIO Logo动画 -->
      <div class="logo-animation">
        <div class="logo-text">
          <span class="letter" v-for="(letter, index) in logoLetters" :key="index" :style="{ animationDelay: `${index * 0.1}s` }">
            {{ letter }}
          </span>
        </div>
        <div class="logo-subtitle animate__animated animate__fadeInUp animate__delay-1s">
          Modern International Open University
        </div>
      </div>
      
      <!-- 进度条 -->
      <div class="progress-container">
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: `${progress}%` }"></div>
        </div>
        <div class="progress-text">{{ loadingText }}</div>
      </div>
      
      <!-- 装饰性粒子 -->
      <div class="loading-particles">
        <div v-for="i in 20" :key="i" class="particle" :style="getParticleStyle(i)"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { gsap } from 'gsap'

export default {
  name: 'LoadingScreen',
  emits: ['loaded'],
  setup(props, { emit }) {
    const progress = ref(0)
    const isLoaded = ref(false)
    const logoLetters = ['M', 'I', 'O']
    const loadingTexts = [
      '初始化虚拟世界...',
      '加载二次元引擎...',
      '连接知识网络...',
      '准备学术冒险...',
      '启动MIO系统...'
    ]
    const loadingText = ref(loadingTexts[0])

    const getParticleStyle = (index) => {
      const angle = (360 / 20) * index
      const radius = 100 + Math.random() * 50
      const x = Math.cos(angle * Math.PI / 180) * radius
      const y = Math.sin(angle * Math.PI / 180) * radius
      const delay = Math.random() * 2
      const duration = 2 + Math.random() * 2
      
      return {
        left: `calc(50% + ${x}px)`,
        top: `calc(50% + ${y}px)`,
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`
      }
    }

    const startLoading = () => {
      // 进一步缩短整体加载用时
      const duration = 600
      const startTime = Date.now()
      
      const updateProgress = () => {
        const elapsed = Date.now() - startTime
        const newProgress = Math.min((elapsed / duration) * 100, 100)
        progress.value = newProgress
        
        // 更新加载文本
        const textIndex = Math.floor((newProgress / 100) * loadingTexts.length)
        if (textIndex < loadingTexts.length) {
          loadingText.value = loadingTexts[textIndex]
        }
        
        if (newProgress < 100) {
          requestAnimationFrame(updateProgress)
        } else {
          setTimeout(() => {
            isLoaded.value = true
            // 更快的淡出
            setTimeout(() => {
              emit('loaded')
            }, 100)
          }, 50)
        }
      }
      
      requestAnimationFrame(updateProgress)
    }

    onMounted(() => {
      // 优化GSAP动画速度
      gsap.fromTo('.letter', 
        { opacity: 0, y: 50, rotationX: -90 },
        { 
          opacity: 1, 
          y: 0, 
          rotationX: 0,
          duration: 0.5,
          stagger: 0.08,
          ease: "back.out(1.7)"
        }
      )
      
      // 更快启动加载
      setTimeout(startLoading, 50)
    })

    return {
      progress,
      isLoaded,
      logoLetters,
      loadingText,
      getParticleStyle
    }
  }
}
</script>

<style lang="scss" scoped>
.loading-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  transition: opacity 0.3s ease;

  &.fade-out {
    opacity: 0;
    pointer-events: none; // 透明淡出时不再拦截点击
    visibility: hidden;   // 确保从可见性上也移除
  }
}

.loading-container {
  text-align: center;
  position: relative;
}

.logo-animation {
  margin-bottom: 3rem;
}

.logo-text {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.letter {
  font-family: 'Orbitron', sans-serif;
  font-size: 4rem;
  font-weight: 900;
  color: white;
  text-shadow: 0 0 20px rgba(255, 255, 255, 0.5);
  animation: float 2s ease-in-out infinite;
  
  &:nth-child(1) { animation-delay: 0s; }
  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.4s; }
}

.logo-subtitle {
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 300;
  letter-spacing: 2px;
}

.progress-container {
  width: 300px;
  margin: 0 auto;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 1rem;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #00d4ff, #90e0ef);
  border-radius: 2px;
  transition: width 0.3s ease;
  box-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
}

.progress-text {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  font-weight: 300;
}

.loading-particles {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background: white;
  border-radius: 50%;
  opacity: 0.6;
  animation: particle-float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
}

@keyframes particle-float {
  0%, 100% { 
    opacity: 0.6;
    transform: translateY(0px) scale(1);
  }
  50% { 
    opacity: 1;
    transform: translateY(-20px) scale(1.2);
  }
}
</style>
